(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Reveal on scroll ---------- */

  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-in');
              revealObserver.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -6% 0px', threshold: 0.06 }
        )
      : null;

  const initReveal = (root = document) => {
    root.querySelectorAll('.m-reveal:not(.is-in)').forEach((el) => {
      if (revealObserver && !reduceMotion.matches) revealObserver.observe(el);
      else el.classList.add('is-in');
    });
  };

  /* ---------- Hero slideshow ---------- */

  class MaduSlideshow extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.count = this.slides.length;
      this.index = 0;
      this.elapsed = 0;
      this.last = null;
      this.paused = false;
      this.inView = true;
      this.delay = Number(this.dataset.autoplay || 0) * 1000;
      this.current = this.querySelector('[data-current]');
      this.bar = this.querySelector('[data-progress]');

      if (!this.track || this.count < 2) return;

      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));

      this.addEventListener('keydown', (event) => {
        if (event.target.closest('input, textarea, select')) return;
        if (event.key === 'ArrowLeft') this.go(this.index - 1);
        if (event.key === 'ArrowRight') this.go(this.index + 1);
      });

      let startX = null;
      let startY = null;
      this.track.addEventListener(
        'pointerdown',
        (event) => {
          if (event.pointerType === 'mouse') return;
          startX = event.clientX;
          startY = event.clientY;
        },
        { passive: true }
      );
      this.track.addEventListener('pointerup', (event) => {
        if (startX === null) return;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        startX = null;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) this.go(this.index + (dx < 0 ? 1 : -1));
      });
      this.track.addEventListener('pointercancel', () => (startX = null));

      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') this.paused = true;
      });
      this.addEventListener('pointerleave', () => (this.paused = false));
      this.addEventListener('focusin', () => (this.paused = true));
      this.addEventListener('focusout', () => (this.paused = false));

      this.addEventListener('shopify:block:select', (event) => {
        const index = this.slides.indexOf(event.target.closest('[data-slide]'));
        if (index < 0) return;
        this.editorHold = true;
        this.go(index);
      });
      this.addEventListener('shopify:block:deselect', () => (this.editorHold = false));

      if ('IntersectionObserver' in window) {
        this.viewObserver = new IntersectionObserver(([entry]) => (this.inView = entry.isIntersecting));
        this.viewObserver.observe(this);
      }

      this.go(0);

      if (this.delay > 0 && !reduceMotion.matches) {
        this.tick = this.tick.bind(this);
        this.raf = requestAnimationFrame(this.tick);
      }
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.raf);
      this.viewObserver?.disconnect();
    }

    tick(time) {
      if (this.last === null) this.last = time;
      const delta = Math.min(time - this.last, 100);
      this.last = time;

      if (!this.paused && !this.editorHold && this.inView && !document.hidden) this.elapsed += delta;

      const progress = Math.min(this.elapsed / this.delay, 1);
      if (this.bar) this.bar.style.transform = `scaleX(${progress})`;
      if (progress >= 1) this.go(this.index + 1);

      this.raf = requestAnimationFrame(this.tick);
    }

    go(target) {
      const index = (target + this.count) % this.count;

      this.slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        slide.toggleAttribute('inert', !active);
        slide.setAttribute('aria-hidden', String(!active));
        const video = slide.querySelector('video');
        if (video) {
          if (active) video.play?.().catch(() => {});
          else video.pause?.();
        }
      });

      this.track.style.setProperty('--m-index', index);
      this.index = index;
      this.elapsed = 0;

      if (this.current) this.current.textContent = String(index + 1).padStart(2, '0');
      if (this.bar && !(this.delay > 0 && !reduceMotion.matches)) {
        this.bar.style.transform = `scaleX(${(index + 1) / this.count})`;
      }
    }
  }

  /* ---------- Horizontal rail ---------- */

  class MaduRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-rail-track]');
      if (!this.track) return;

      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      this.update = this.update.bind(this);

      this.prev?.addEventListener('click', () => this.step(-1));
      this.next?.addEventListener('click', () => this.step(1));
      this.track.addEventListener('scroll', this.update, { passive: true });

      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.track);
      this.update();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
    }

    step(direction) {
      const item = this.track.firstElementChild;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const size = item ? item.getBoundingClientRect().width + gap : this.track.clientWidth;
      const perView = Math.max(1, Math.floor((this.track.clientWidth + gap) / size));
      this.track.scrollBy({ left: direction * size * perView, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }

    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const max = scrollWidth - clientWidth;
      const overflow = max > 4;

      this.dataset.overflow = String(overflow);
      if (this.prev) this.prev.disabled = !overflow || scrollLeft <= 4;
      if (this.next) this.next.disabled = !overflow || scrollLeft >= max - 4;

      const thumb = scrollWidth > 0 ? Math.min(clientWidth / scrollWidth, 1) : 1;
      const offset = max > 0 ? (scrollLeft / max) * ((1 - thumb) / thumb) * 100 : 0;
      this.style.setProperty('--m-rail-thumb', `${thumb * 100}%`);
      this.style.setProperty('--m-rail-x', `${offset}%`);
    }
  }

  if (!customElements.get('madu-slideshow')) customElements.define('madu-slideshow', MaduSlideshow);
  if (!customElements.get('madu-rail')) customElements.define('madu-rail', MaduRail);

  initReveal();
  document.addEventListener('shopify:section:load', (event) => initReveal(event.target));
  window.maduReady = true;
})();
